import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  ThemeProvider,
  createTheme,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tabs,
  Tab,
  Link,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  HelpOutline as HelpIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";

// Zoho Books-inspired theme
const theme = createTheme({
  typography: {
    fontFamily: '"Poppins", "Lato", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
  },
  palette: {
    primary: {
      main: "#2D7FF9",
      light: "#5B9EFA",
      dark: "#1E5FD9",
    },
    success: {
      main: "#0BBE87",
    },
    error: {
      main: "#E85858",
    },
    background: {
      default: "#F9FAFB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#212529",
      secondary: "#6C757D",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#FFFFFF",
            "& fieldset": {
              borderColor: "#D1D5DB",
            },
            "&:hover fieldset": {
              borderColor: "#9CA3AF",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#2D7FF9",
              borderWidth: "1px",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "13px",
            padding: "10px 12px",
          },
          "& .MuiInputLabel-root": {
            fontSize: "13px",
            color: "#6C757D",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 500,
          padding: "8px 16px",
          borderRadius: "4px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "13px",
          padding: "12px 16px",
          borderBottom: "1px solid #E5E7EB",
        },
        head: {
          fontWeight: 600,
          color: "#374151",
          backgroundColor: "#F9FAFB",
          borderBottom: "2px solid #E5E7EB",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: "6px",
        },
      },
    },
  },
});

// Mock data for institutes
const initialInstitutes = [
  {
    id: 1,
    name: "Tech University",
    address: "123 University Street, Tech City",
    gstin: "29ABCDE1234F1Z5",
    website: "www.techuniversity.edu",
  },
  {
    id: 2,
    name: "Business School",
    address: "456 Commerce Road, Business District",
    gstin: "27XYZAB5678G2Y6",
    website: "www.businessschool.edu",
  },
  {
    id: 3,
    name: "Medical College",
    address: "789 Health Avenue, Medical Park",
    gstin: "19LMNOP9012H3W7",
    website: "www.medicalcollege.edu",
  },
];

// Payment terms options
const paymentTerms = [
  "Net 15",
  "Net 30",
  "Net 45",
  "Net 60",
  "Due on Receipt",
  "Due end of the month",
  "Due end of next month",
];

// Indian states
const indianStates = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

// Custom Label Component matching Zoho design with inline layout
const FieldLabel = ({ children, required = false, width = "140px" }) => (
  <Typography
    component="label"
    sx={{
      fontSize: "13px",
      fontWeight: 400,
      color: "#212529",
      display: "inline-flex",
      alignItems: "center",
      width: width,
      minWidth: width,
      flexShrink: 0,
      textAlign: "left",
      paddingRight: "12px",
      paddingTop: "8px", // Align with input field
      "& .required": {
        color: "#DC2626",
        ml: 0.25,
      },
    }}
  >
    {children}
    {required && <span className="required">*</span>}
  </Typography>
);

// Form Field Component with inline layout - label on left, input on right
const FormField = ({ children, sx = {} }) => (
  <Box
    sx={{ mb: 1.5, display: "flex", alignItems: "flex-start", gap: 0, ...sx }}
  >
    {children}
  </Box>
);

const InvoiceFormComponent = () => {
  // Institute selection and editing
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [instituteData, setInstituteData] = useState({
    name: "",
    address: "",
    gstin: "",
    website: "",
  });
  const [isEditingInstitute, setIsEditingInstitute] = useState(false);
  const [editedInstituteData, setEditedInstituteData] = useState({});

  // Invoice details
  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceNo: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    terms: "Net 30",
    dueDate: "",
  });

  // Item table states
  const [items, setItems] = useState([
    { id: 1, details: "", quantity: 1.0, rate: 0.0, amount: 0.0 },
  ]);
  const [discount, setDiscount] = useState({ value: 0, type: "%" });
  const [taxType, setTaxType] = useState("TDS");
  const [selectedTax, setSelectedTax] = useState("");
  const [adjustment, setAdjustment] = useState(0);
  const [customerNotes, setCustomerNotes] = useState(
    "Thanks for your business."
  );

  // Dialog for creating new institute
  const [openCreateInstituteDialog, setOpenCreateInstituteDialog] =
    useState(false);
  const [newInstitute, setNewInstitute] = useState({
    name: "",
    address: "",
    gstin: "",
    website: "",
  });

  // Create/Edit Institute Form States
  const [mainTab, setMainTab] = useState(0);
  const [selectedState, setSelectedState] = useState(null);
  const [instituteType, setInstituteType] = useState(null);
  const [currentInstituteTypes, setCurrentInstituteTypes] = useState([
    "Engineering",
    "Degree",
    "University",
    "Diploma",
  ]);
  const [openInstituteTypeDialog, setOpenInstituteTypeDialog] = useState(false);
  const [newInstituteType, setNewInstituteType] = useState("");

  // Billing Address State
  const [billingAttention, setBillingAttention] = useState("");
  const [billingCountry, setBillingCountry] = useState(null);
  const [billingAddressLine1, setBillingAddressLine1] = useState("");
  const [billingAddressLine2, setBillingAddressLine2] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingState, setBillingState] = useState(null);
  const [billingPincode, setBillingPincode] = useState("");
  const [billingPhone, setBillingPhone] = useState("");
  const [billingFax, setBillingFax] = useState("");

  // Shipping Address State
  const [shippingAttention, setShippingAttention] = useState("");
  const [shippingCountry, setShippingCountry] = useState(null);
  const [shippingAddressLine1, setShippingAddressLine1] = useState("");
  const [shippingAddressLine2, setShippingAddressLine2] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingState, setShippingState] = useState(null);
  const [shippingPincode, setShippingPincode] = useState("");
  const [shippingPhone, setShippingPhone] = useState("");
  const [shippingFax, setShippingFax] = useState("");

  // Other Details State
  const [website, setWebsite] = useState("");
  const [gstinNo, setGstinNo] = useState("");

  // Handle institute selection
  const handleInstituteChange = (event, newValue) => {
    if (newValue?.isCreateNew) {
      setOpenCreateInstituteDialog(true);
      return;
    }

    setSelectedInstitute(newValue);
    if (newValue) {
      setInstituteData({
        name: newValue.name,
        address: newValue.address,
        gstin: newValue.gstin,
        website: newValue.website,
      });
    } else {
      setInstituteData({
        name: "",
        address: "",
        gstin: "",
        website: "",
      });
    }
  };

  // Handle edit institute
  const handleEditInstitute = () => {
    setEditedInstituteData({ ...instituteData });
    setIsEditingInstitute(true);
  };

  const handleSaveInstituteEdit = () => {
    setInstituteData({ ...editedInstituteData });
    setIsEditingInstitute(false);
  };

  const handleCancelInstituteEdit = () => {
    setEditedInstituteData({});
    setIsEditingInstitute(false);
  };

  // Handle invoice details change
  const handleInvoiceDetailChange = (field, value) => {
    setInvoiceDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Auto-calculate due date based on terms
    if (field === "terms" || field === "invoiceDate") {
      const date = new Date(
        field === "invoiceDate" ? value : invoiceDetails.invoiceDate
      );
      const term = field === "terms" ? value : invoiceDetails.terms;

      let daysToAdd = 0;
      if (term.includes("Net")) {
        daysToAdd = parseInt(term.split(" ")[1]);
      } else if (term === "Due on Receipt") {
        daysToAdd = 0;
      }

      const dueDate = new Date(date);
      dueDate.setDate(dueDate.getDate() + daysToAdd);
      setInvoiceDetails((prev) => ({
        ...prev,
        dueDate: dueDate.toISOString().split("T")[0],
      }));
    }
  };

  // Item table functions
  const handleAddItem = () => {
    setItems([
      ...items,
      {
        id: Date.now(),
        details: "",
        quantity: 1.0,
        rate: 0.0,
        amount: 0.0,
      },
    ]);
  };

  const handleDeleteItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== id));
    }
  };

  const handleItemChange = (id, field, value) => {
    setItems(
      items.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          if (field === "quantity" || field === "rate") {
            updatedItem.amount = (
              parseFloat(updatedItem.quantity || 0) *
              parseFloat(updatedItem.rate || 0)
            ).toFixed(2);
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  // Calculations
  const calculateSubTotal = () => {
    return items
      .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
      .toFixed(2);
  };

  const calculateDiscount = () => {
    const subTotal = parseFloat(calculateSubTotal());
    if (discount.type === "%") {
      return ((subTotal * parseFloat(discount.value || 0)) / 100).toFixed(2);
    }
    return parseFloat(discount.value || 0).toFixed(2);
  };

  const calculateTotal = () => {
    const subTotal = parseFloat(calculateSubTotal());
    const discountAmount = parseFloat(calculateDiscount());
    const adjustmentAmount = parseFloat(adjustment || 0);
    return (subTotal - discountAmount + adjustmentAmount).toFixed(2);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#F9FAFB",
          py: 4,
          px: 3,
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#212529",
                mb: 0.5,
              }}
            >
              Create Invoice
            </Typography>
            <Typography variant="body2" sx={{ color: "#6C757D" }}>
              Generate professional invoices for your clients
            </Typography>
          </Box>

          {/* Main Form */}
          <Paper sx={{ p: 3, mb: 3 }}>
            {/* Institute Selection Section */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#374151",
                  mb: 1.5,
                  fontSize: "13px",
                }}
              >
                Institute Details
              </Typography>

              <Autocomplete
                value={selectedInstitute}
                onChange={handleInstituteChange}
                options={[
                  {
                    id: "create-new",
                    name: "Create New Institute...",
                    isCreateNew: true,
                  },
                  ...initialInstitutes,
                ]}
                getOptionLabel={(option) => option.name || ""}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.isCreateNew ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                          py: 0.5,
                        }}
                      >
                        <AddIcon sx={{ fontSize: 18, color: "#2D7FF9" }} />
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#2D7FF9",
                            fontWeight: 600,
                          }}
                        >
                          {option.name}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: "13px" }}>
                        {option.name}
                      </Typography>
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select or create institute"
                    size="small"
                  />
                )}
                sx={{ maxWidth: 500 }}
              />

              {/* Institute Details Display/Edit */}
              {selectedInstitute && !selectedInstitute.isCreateNew && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2.5,
                    backgroundColor: "#F9FAFB",
                    borderRadius: "6px",
                    border: "1px solid #E5E7EB",
                    position: "relative",
                  }}
                >
                  {!isEditingInstitute && (
                    <IconButton
                      size="small"
                      onClick={handleEditInstitute}
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        color: "#2D7FF9",
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}

                  {isEditingInstitute ? (
                    <Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Institute Name"
                            value={editedInstituteData.name || ""}
                            onChange={(e) =>
                              setEditedInstituteData({
                                ...editedInstituteData,
                                name: e.target.value,
                              })
                            }
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address"
                            value={editedInstituteData.address || ""}
                            onChange={(e) =>
                              setEditedInstituteData({
                                ...editedInstituteData,
                                address: e.target.value,
                              })
                            }
                            multiline
                            rows={2}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="GSTIN"
                            value={editedInstituteData.gstin || ""}
                            onChange={(e) =>
                              setEditedInstituteData({
                                ...editedInstituteData,
                                gstin: e.target.value,
                              })
                            }
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Website"
                            value={editedInstituteData.website || ""}
                            onChange={(e) =>
                              setEditedInstituteData({
                                ...editedInstituteData,
                                website: e.target.value,
                              })
                            }
                            size="small"
                          />
                        </Grid>
                      </Grid>
                      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                          onClick={handleSaveInstituteEdit}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleCancelInstituteEdit}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: 600,
                            color: "#212529",
                            mb: 1,
                          }}
                        >
                          {instituteData.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "#9CA3AF",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              mb: 0.5,
                            }}
                          >
                            Address
                          </Typography>
                          <Typography
                            sx={{ fontSize: "13px", color: "#374151" }}
                          >
                            {instituteData.address}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "#9CA3AF",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              mb: 0.5,
                            }}
                          >
                            GSTIN
                          </Typography>
                          <Typography
                            sx={{ fontSize: "13px", color: "#374151" }}
                          >
                            {instituteData.gstin}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "#9CA3AF",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              mb: 0.5,
                            }}
                          >
                            Website
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#2D7FF9",
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {instituteData.website}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Invoice Details Section */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#374151",
                  mb: 2,
                  fontSize: "13px",
                }}
              >
                Invoice Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Invoice No."
                    value={invoiceDetails.invoiceNo}
                    onChange={(e) =>
                      handleInvoiceDetailChange("invoiceNo", e.target.value)
                    }
                    placeholder="INV-001"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Invoice Date"
                    type="date"
                    value={invoiceDetails.invoiceDate}
                    onChange={(e) =>
                      handleInvoiceDetailChange("invoiceDate", e.target.value)
                    }
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarIcon
                            sx={{ fontSize: 18, color: "#9CA3AF" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <TextField
                      select
                      label="Terms"
                      value={invoiceDetails.terms}
                      onChange={(e) =>
                        handleInvoiceDetailChange("terms", e.target.value)
                      }
                      size="small"
                    >
                      {paymentTerms.map((term) => (
                        <MenuItem
                          key={term}
                          value={term}
                          sx={{ fontSize: "13px" }}
                        >
                          {term}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Due Date"
                    type="date"
                    value={invoiceDetails.dueDate}
                    onChange={(e) =>
                      handleInvoiceDetailChange("dueDate", e.target.value)
                    }
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarIcon
                            sx={{ fontSize: 18, color: "#9CA3AF" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Item Table Section */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#374151",
                    fontSize: "14px",
                  }}
                >
                  Item Table
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
                    sx={{ fontSize: "13px" }}
                  >
                    Scan Item
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    sx={{ fontSize: "13px" }}
                  >
                    Bulk Actions
                  </Button>
                </Box>
              </Box>

              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ border: "1px solid #E5E7EB", mb: 2 }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "45%" }}>ITEM DETAILS</TableCell>
                      <TableCell sx={{ width: "15%", textAlign: "center" }}>
                        QUANTITY
                      </TableCell>
                      <TableCell sx={{ width: "15%", textAlign: "right" }}>
                        RATE
                      </TableCell>
                      <TableCell sx={{ width: "20%", textAlign: "right" }}>
                        AMOUNT
                      </TableCell>
                      <TableCell
                        sx={{ width: "5%", textAlign: "center" }}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <TextField
                            fullWidth
                            placeholder="Type or click to select an item."
                            value={item.details}
                            onChange={(e) =>
                              handleItemChange(
                                item.id,
                                "details",
                                e.target.value
                              )
                            }
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                              sx: { fontSize: "13px", color: "#6C757D" },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <TextField
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChange(
                                item.id,
                                "quantity",
                                e.target.value
                              )
                            }
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                              sx: {
                                fontSize: "13px",
                                textAlign: "center",
                                "& input": { textAlign: "center" },
                              },
                            }}
                            sx={{ width: "80px" }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          <TextField
                            type="number"
                            value={item.rate}
                            onChange={(e) =>
                              handleItemChange(item.id, "rate", e.target.value)
                            }
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                              sx: {
                                fontSize: "13px",
                                textAlign: "right",
                                "& input": { textAlign: "right" },
                              },
                            }}
                            sx={{ width: "100px" }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          <Typography
                            sx={{ fontSize: "13px", fontWeight: 600 }}
                          >
                            {item.amount}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteItem(item.id)}
                            disabled={items.length === 1}
                            sx={{ color: "#E85858" }}
                          >
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={handleAddItem}
                  sx={{
                    fontSize: "13px",
                    backgroundColor: "#E8F2FF",
                    color: "#2D7FF9",
                    "&:hover": {
                      backgroundColor: "#D1E7FF",
                    },
                  }}
                >
                  Add New Row
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  sx={{ fontSize: "13px" }}
                >
                  Add Items in Bulk
                </Button>
              </Box>

              {/* Totals Section */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 4,
                }}
              >
                {/* Left side - Customer Notes */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#374151",
                      mb: 1,
                    }}
                  >
                    Customer Notes
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    placeholder="Thanks for your business."
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "13px",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#9CA3AF",
                      mt: 0.5,
                      fontStyle: "italic",
                    }}
                  >
                    Will be displayed on the invoice
                  </Typography>
                </Box>

                {/* Right side - Calculations */}
                <Box sx={{ width: "400px" }}>
                  {/* Sub Total */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.5,
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#374151",
                      }}
                    >
                      Sub Total
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#212529",
                      }}
                    >
                      {calculateSubTotal()}
                    </Typography>
                  </Box>

                  {/* Discount */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.5,
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#374151",
                      }}
                    >
                      Discount
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        type="number"
                        value={discount.value}
                        onChange={(e) =>
                          setDiscount({ ...discount, value: e.target.value })
                        }
                        size="small"
                        sx={{
                          width: "80px",
                          "& .MuiOutlinedInput-root": {
                            fontSize: "13px",
                            "& input": { textAlign: "right", padding: "8px" },
                          },
                        }}
                      />
                      <Select
                        value={discount.type}
                        onChange={(e) =>
                          setDiscount({ ...discount, type: e.target.value })
                        }
                        size="small"
                        sx={{
                          width: "60px",
                          fontSize: "13px",
                          "& .MuiSelect-select": { padding: "8px" },
                        }}
                      >
                        <MenuItem value="%" sx={{ fontSize: "13px" }}>
                          %
                        </MenuItem>
                        <MenuItem value="fixed" sx={{ fontSize: "13px" }}>
                          ₹
                        </MenuItem>
                      </Select>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#212529",
                          minWidth: "60px",
                          textAlign: "right",
                        }}
                      >
                        {calculateDiscount()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Tax */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.5,
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <RadioGroup
                        row
                        value={taxType}
                        onChange={(e) => setTaxType(e.target.value)}
                      >
                        <FormControlLabel
                          value="TDS"
                          control={
                            <Radio
                              size="small"
                              sx={{
                                p: 0.5,
                                "& .MuiSvgIcon-root": { fontSize: 18 },
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: "13px", ml: 0.5 }}>
                              TDS
                            </Typography>
                          }
                          sx={{ mr: 2 }}
                        />
                        <FormControlLabel
                          value="TCS"
                          control={
                            <Radio
                              size="small"
                              sx={{
                                p: 0.5,
                                "& .MuiSvgIcon-root": { fontSize: 18 },
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: "13px", ml: 0.5 }}>
                              TCS
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={selectedTax}
                          onChange={(e) => setSelectedTax(e.target.value)}
                          displayEmpty
                          sx={{ fontSize: "13px" }}
                        >
                          <MenuItem value="" sx={{ fontSize: "13px" }}>
                            Select a Tax
                          </MenuItem>
                          <MenuItem value="GST 18%" sx={{ fontSize: "13px" }}>
                            GST 18%
                          </MenuItem>
                          <MenuItem value="GST 12%" sx={{ fontSize: "13px" }}>
                            GST 12%
                          </MenuItem>
                          <MenuItem value="GST 5%" sx={{ fontSize: "13px" }}>
                            GST 5%
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#212529",
                          minWidth: "60px",
                          textAlign: "right",
                        }}
                      >
                        - 0.00
                      </Typography>
                    </Box>
                  </Box>

                  {/* Adjustment */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.5,
                      borderBottom: "2px solid #E5E7EB",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#374151",
                        }}
                      >
                        Adjustment
                      </Typography>
                      <IconButton size="small" sx={{ p: 0 }}>
                        <HelpIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        type="number"
                        value={adjustment}
                        onChange={(e) => setAdjustment(e.target.value)}
                        size="small"
                        sx={{
                          width: "100px",
                          "& .MuiOutlinedInput-root": {
                            fontSize: "13px",
                            "& input": { textAlign: "right", padding: "8px" },
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#212529",
                          minWidth: "60px",
                          textAlign: "right",
                        }}
                      >
                        {parseFloat(adjustment || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Total */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "#212529",
                      }}
                    >
                      Total ( ₹ )
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#212529",
                      }}
                    >
                      {calculateTotal()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" size="medium">
              Cancel
            </Button>
            <Button
              variant="contained"
              size="medium"
              startIcon={<SaveIcon />}
              disabled={!selectedInstitute || !invoiceDetails.invoiceNo}
            >
              Save Invoice
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Create/Edit Institute Dialog */}
      <Dialog
        open={openCreateInstituteDialog}
        onClose={() => setOpenCreateInstituteDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            borderBottom: "1px solid #E5E7EB",
            pb: 2,
          }}
        >
          Create New Institute
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {/* Institute Basic Details */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ maxWidth: "600px" }}>
              {/* State */}
              <FormField>
                <FieldLabel required>State</FieldLabel>
                <Autocomplete
                  value={selectedState}
                  onChange={(event, newValue) => setSelectedState(newValue)}
                  options={indianStates}
                  sx={{
                    flex: 1,
                    maxWidth: "350px",
                    "& .MuiAutocomplete-input": {
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-root": {
                      paddingRight: "9px !important",
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select a State"
                      size="small"
                    />
                  )}
                />
              </FormField>

              {/* Institute Name */}
              <FormField>
                <FieldLabel required>Institute Name</FieldLabel>
                <TextField
                  placeholder="Enter Institute Name"
                  value={newInstitute.name}
                  onChange={(e) =>
                    setNewInstitute({ ...newInstitute, name: e.target.value })
                  }
                  size="small"
                  sx={{ flex: 1, maxWidth: "350px" }}
                />
              </FormField>

              {/* Institute Type */}
              <FormField>
                <FieldLabel>Institute Type</FieldLabel>
                <Autocomplete
                  value={instituteType}
                  onChange={(event, newValue) => {
                    if (newValue === "Create New") {
                      setOpenInstituteTypeDialog(true);
                    } else {
                      setInstituteType(newValue);
                    }
                  }}
                  options={[...currentInstituteTypes, "Create New"]}
                  sx={{
                    flex: 1,
                    maxWidth: "350px",
                    "& .MuiAutocomplete-input": {
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-root": {
                      paddingRight: "9px !important",
                    },
                  }}
                  renderOption={(props, option) => {
                    if (option === "Create New") {
                      return (
                        <Box
                          {...props}
                          sx={{
                            color: "#2D7FF9 !important",
                            fontWeight: 500,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                          Create New
                        </Box>
                      );
                    }
                    return <Box {...props}>{option}</Box>;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Institute Type"
                      size="small"
                    />
                  )}
                />
              </FormField>
            </Box>
          </Box>

          {/* Tabs */}
          <Box>
            <Tabs
              value={mainTab}
              onChange={(event, newValue) => setMainTab(newValue)}
              sx={{
                borderBottom: "1px solid #E5E7EB",
                mb: 3,
                "& .MuiTabs-indicator": {
                  backgroundColor: "#2D7FF9",
                },
              }}
            >
              <Tab
                label="Addresses"
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#6C757D",
                  "&.Mui-selected": {
                    color: "#2D7FF9",
                  },
                }}
              />
              <Tab
                label="Other Details"
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#6C757D",
                  "&.Mui-selected": {
                    color: "#2D7FF9",
                  },
                }}
              />
            </Tabs>

            {/* Addresses Tab */}
            {mainTab === 0 && (
              <Grid container spacing={4}>
                {/* Billing Address Column */}
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#212529",
                      mb: 2,
                    }}
                  >
                    Billing Address
                  </Typography>

                  {/* Attention */}
                  <FormField>
                    <FieldLabel>Attention</FieldLabel>
                    <TextField
                      placeholder=""
                      value={billingAttention}
                      onChange={(e) => setBillingAttention(e.target.value)}
                      size="small"
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* Country/Region */}
                  <FormField>
                    <FieldLabel>Country/Region</FieldLabel>
                    <Autocomplete
                      value={billingCountry}
                      onChange={(event, newValue) =>
                        setBillingCountry(newValue)
                      }
                      options={["India", "USA", "UK", "Canada"]}
                      sx={{ flex: 1, maxWidth: "300px" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select"
                          size="small"
                        />
                      )}
                    />
                  </FormField>

                  {/* Address Line 1 */}
                  <FormField sx={{ alignItems: "flex-start" }}>
                    <FieldLabel>Address</FieldLabel>
                    <TextField
                      placeholder="Street 1"
                      value={billingAddressLine1}
                      onChange={(e) => setBillingAddressLine1(e.target.value)}
                      multiline
                      rows={2}
                      maxRows={4}
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* Address Line 2 */}
                  <FormField sx={{ alignItems: "flex-start" }}>
                    <Box sx={{ width: "140px", minWidth: "140px" }}></Box>
                    <TextField
                      placeholder="Street 2"
                      value={billingAddressLine2}
                      onChange={(e) => setBillingAddressLine2(e.target.value)}
                      multiline
                      rows={2}
                      maxRows={4}
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* City */}
                  <FormField>
                    <FieldLabel>City</FieldLabel>
                    <TextField
                      placeholder=""
                      value={billingCity}
                      onChange={(e) => setBillingCity(e.target.value)}
                      size="small"
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* State */}
                  <FormField>
                    <FieldLabel>State</FieldLabel>
                    <Autocomplete
                      value={billingState}
                      onChange={(event, newValue) => setBillingState(newValue)}
                      options={indianStates}
                      sx={{ flex: 1, maxWidth: "300px" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select or type to add"
                          size="small"
                        />
                      )}
                    />
                  </FormField>

                  {/* Pin Code */}
                  <FormField>
                    <FieldLabel>Pin Code</FieldLabel>
                    <TextField
                      placeholder=""
                      value={billingPincode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 6) {
                          setBillingPincode(value);
                        }
                      }}
                      size="small"
                      inputProps={{ maxLength: 6 }}
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* Phone */}
                  <FormField>
                    <FieldLabel>Phone</FieldLabel>
                    <TextField
                      placeholder=""
                      value={billingPhone}
                      onChange={(e) => setBillingPhone(e.target.value)}
                      size="small"
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* Fax Number */}
                  <FormField>
                    <FieldLabel>Fax Number</FieldLabel>
                    <TextField
                      placeholder=""
                      value={billingFax}
                      onChange={(e) => setBillingFax(e.target.value)}
                      size="small"
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>
                </Grid>

                {/* Shipping Address Column */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#212529",
                      }}
                    >
                      Shipping Address
                    </Typography>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => {
                        setShippingAttention(billingAttention);
                        setShippingCountry(billingCountry);
                        setShippingAddressLine1(billingAddressLine1);
                        setShippingAddressLine2(billingAddressLine2);
                        setShippingCity(billingCity);
                        setShippingState(billingState);
                        setShippingPincode(billingPincode);
                        setShippingPhone(billingPhone);
                        setShippingFax(billingFax);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "#2D7FF9",
                        fontSize: "12px",
                        textDecoration: "none",
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                      Copy billing address
                    </Link>
                  </Box>

                  {/* Attention */}
                  <FormField>
                    <FieldLabel>Attention</FieldLabel>
                    <TextField
                      placeholder=""
                      value={shippingAttention}
                      onChange={(e) => setShippingAttention(e.target.value)}
                      size="small"
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* Country/Region */}
                  <FormField>
                    <FieldLabel>Country/Region</FieldLabel>
                    <Autocomplete
                      value={shippingCountry}
                      onChange={(event, newValue) =>
                        setShippingCountry(newValue)
                      }
                      options={["India", "USA", "UK", "Canada"]}
                      sx={{ flex: 1, maxWidth: "300px" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select"
                          size="small"
                        />
                      )}
                    />
                  </FormField>

                  {/* Address Line 1 */}
                  <FormField sx={{ alignItems: "flex-start" }}>
                    <FieldLabel>Address</FieldLabel>
                    <TextField
                      placeholder="Street 1"
                      value={shippingAddressLine1}
                      onChange={(e) => setShippingAddressLine1(e.target.value)}
                      multiline
                      rows={2}
                      maxRows={4}
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* Address Line 2 */}
                  <FormField sx={{ alignItems: "flex-start" }}>
                    <Box sx={{ width: "140px", minWidth: "140px" }}></Box>
                    <TextField
                      placeholder="Street 2"
                      value={shippingAddressLine2}
                      onChange={(e) => setShippingAddressLine2(e.target.value)}
                      multiline
                      rows={2}
                      maxRows={4}
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* City */}
                  <FormField>
                    <FieldLabel>City</FieldLabel>
                    <TextField
                      placeholder=""
                      value={shippingCity}
                      onChange={(e) => setShippingCity(e.target.value)}
                      size="small"
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* State */}
                  <FormField>
                    <FieldLabel>State</FieldLabel>
                    <Autocomplete
                      value={shippingState}
                      onChange={(event, newValue) => setShippingState(newValue)}
                      options={indianStates}
                      sx={{ flex: 1, maxWidth: "300px" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select or type to add"
                          size="small"
                        />
                      )}
                    />
                  </FormField>

                  {/* Pin Code */}
                  <FormField>
                    <FieldLabel>Pin Code</FieldLabel>
                    <TextField
                      placeholder=""
                      value={shippingPincode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 6) {
                          setShippingPincode(value);
                        }
                      }}
                      size="small"
                      inputProps={{ maxLength: 6 }}
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* Phone */}
                  <FormField>
                    <FieldLabel>Phone</FieldLabel>
                    <TextField
                      placeholder=""
                      value={shippingPhone}
                      onChange={(e) => setShippingPhone(e.target.value)}
                      size="small"
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>

                  {/* Fax Number */}
                  <FormField>
                    <FieldLabel>Fax Number</FieldLabel>
                    <TextField
                      placeholder=""
                      value={shippingFax}
                      onChange={(e) => setShippingFax(e.target.value)}
                      size="small"
                      sx={{ flex: 1, maxWidth: "300px" }}
                    />
                  </FormField>
                </Grid>
              </Grid>
            )}

            {/* Other Details Tab */}
            {mainTab === 1 && (
              <Box sx={{ maxWidth: "600px" }}>
                {/* Website */}
                <FormField>
                  <FieldLabel>Institute Website</FieldLabel>
                  <TextField
                    placeholder="https://www.example.com"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    type="url"
                    size="small"
                    sx={{ flex: 1, maxWidth: "350px" }}
                  />
                </FormField>

                {/* GSTIN No */}
                <FormField>
                  <FieldLabel>GSTIN No.</FieldLabel>
                  <TextField
                    placeholder="Enter 15-digit GSTIN"
                    value={gstinNo}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      if (value.length <= 15) {
                        setGstinNo(value);
                      }
                    }}
                    size="small"
                    inputProps={{ maxLength: 15 }}
                    sx={{ flex: 1, maxWidth: "350px" }}
                  />
                </FormField>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: "1px solid #E5E7EB" }}>
          <Button
            onClick={() => {
              setOpenCreateInstituteDialog(false);
              setNewInstitute({
                name: "",
                address: "",
                gstin: "",
                website: "",
              });
              setSelectedState(null);
              setInstituteType(null);
              setMainTab(0);
              // Reset all form fields
              setBillingAttention("");
              setBillingCountry(null);
              setBillingAddressLine1("");
              setBillingAddressLine2("");
              setBillingCity("");
              setBillingState(null);
              setBillingPincode("");
              setBillingPhone("");
              setBillingFax("");
              setShippingAttention("");
              setShippingCountry(null);
              setShippingAddressLine1("");
              setShippingAddressLine2("");
              setShippingCity("");
              setShippingState(null);
              setShippingPincode("");
              setShippingPhone("");
              setShippingFax("");
              setWebsite("");
              setGstinNo("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (newInstitute.name.trim() && selectedState) {
                const instituteToAdd = {
                  id: Date.now(),
                  name: newInstitute.name,
                  address: `${billingAddressLine1}, ${billingCity}, ${
                    billingState || ""
                  }, ${billingPincode}`,
                  gstin: gstinNo,
                  website: website,
                };
                initialInstitutes.push(instituteToAdd);
                setSelectedInstitute(instituteToAdd);
                setInstituteData({
                  name: instituteToAdd.name,
                  address: instituteToAdd.address,
                  gstin: instituteToAdd.gstin,
                  website: instituteToAdd.website,
                });
                setOpenCreateInstituteDialog(false);
                // Reset form
                setNewInstitute({
                  name: "",
                  address: "",
                  gstin: "",
                  website: "",
                });
                setSelectedState(null);
                setInstituteType(null);
                setMainTab(0);
                setBillingAttention("");
                setBillingCountry(null);
                setBillingAddressLine1("");
                setBillingAddressLine2("");
                setBillingCity("");
                setBillingState(null);
                setBillingPincode("");
                setBillingPhone("");
                setBillingFax("");
                setShippingAttention("");
                setShippingCountry(null);
                setShippingAddressLine1("");
                setShippingAddressLine2("");
                setShippingCity("");
                setShippingState(null);
                setShippingPincode("");
                setShippingPhone("");
                setShippingFax("");
                setWebsite("");
                setGstinNo("");
              }
            }}
            variant="contained"
            disabled={!newInstitute.name.trim() || !selectedState}
            startIcon={<AddIcon />}
          >
            Create Institute
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Institute Type Dialog */}
      <Dialog
        open={openInstituteTypeDialog}
        onClose={() => setOpenInstituteTypeDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
          Create New Institute Type
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Institute Type Name"
            fullWidth
            variant="outlined"
            value={newInstituteType}
            onChange={(e) => setNewInstituteType(e.target.value)}
            placeholder="Enter new institute type"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setOpenInstituteTypeDialog(false);
              setNewInstituteType("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (newInstituteType.trim()) {
                setCurrentInstituteTypes([
                  ...currentInstituteTypes,
                  newInstituteType,
                ]);
                setInstituteType(newInstituteType);
                setOpenInstituteTypeDialog(false);
                setNewInstituteType("");
              }
            }}
            variant="contained"
            disabled={!newInstituteType.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default InvoiceFormComponent;
